/* eslint-disable max-lines */
import React from 'react';
import { Col, FormGroup, Input, Label, Row, Card, CardBody, CardHeader, InputGroup, InputGroupText } from 'reactstrap';
import NumberFormat from 'react-number-format';

import { selectAllOnFocus } from '../../../../helpers/selectOnFocus';
interface NC4StepThreeFormData {
  filingStatus: 'single_married_separately' | 'head_of_household' | 'married_jointly_surviving_spouse' | '';
  surviving_itemizedDeductionsExceed: 'yes' | 'no' | '';
  surviving_childDeductionExceed: 'yes' | 'no' | '';
  surviving_federalAdjustments: 'yes' | 'no' | '';
  surviving_claimTaxCredits: 'yes' | 'no' | '';
  part2_line1_itemizedDeductions: string;
  part2_line2_standardDeduction: string;
  part2_line3_subtractLine2FromLine1: string;
  part2_line4_childDeductionAmount: string;
  part2_line5_federalAdjustmentsToIncome: string;
  part2_line6_addLines345: string;
  part2_line7_nonwageIncome: string;
  part2_line8_ncAdditionsToAgi: string;
  part2_line9_addLines78: string;
  part2_line10_subtractLine9FromLine6: string;
  part2_line11_divideLine10By2500: string;
  part2_line12_estimatedNcTaxCredits: string;
  part2_line13_divideLine12By109: string;
  part2_line14_additionalAllowances: string;
  part2_line15_totalAllowancesPartII: string;
  part2_line16_spouseAllowancesToClaim: string;
  part2_line17_finalTotalAllowances: string;
  [key: string]: any;
}

interface NC4StepThreeProps {
  formData: NC4StepThreeFormData;
  handleNumericInputChange: (name: string) => (values: any) => void;
  handleRadioChange: (name: string, value: 'yes' | 'no' | '') => void;
}

const WorksheetQuestionRow: React.FC<{
  questionNumber: number;
  questionText: string;
  fieldName: string;
  value: 'yes' | 'no' | '';
  onChange: (fieldName: string, value: 'yes' | 'no' | '') => void;
}> = ({ questionNumber, questionText, fieldName, value, onChange }) => (
  <Row className="mb-2 align-items-center">
    <Col md="9" xs="8">
      <Label className="mb-0" style={{ fontSize: '0.9rem' }}>
        {questionNumber}. {questionText}
      </Label>
    </Col>
    <Col md="3" xs="4" className="text-right">
      <FormGroup check inline className="mb-0 mr-2">
        <Input
          type="radio"
          name={fieldName}
          id={`${fieldName}_yes_step3`}
          value="yes"
          checked={value === 'yes'}
          onChange={() => onChange(fieldName, 'yes')}
        />
        <Label check htmlFor={`${fieldName}_yes_step3`} className="mr-2" style={{ fontSize: '0.85rem' }}>
          Yes
        </Label>
      </FormGroup>
      <FormGroup check inline className="mb-0">
        <Input
          type="radio"
          name={fieldName}
          id={`${fieldName}_no_step3`}
          value="no"
          checked={value === 'no'}
          onChange={() => onChange(fieldName, 'no')}
        />
        <Label check htmlFor={`${fieldName}_no_step3`} style={{ fontSize: '0.85rem' }}>
          No
        </Label>
      </FormGroup>
    </Col>
  </Row>
);

const NC4StepThree: React.FC<NC4StepThreeProps> = ({ formData, handleNumericInputChange, handleRadioChange }) => {
  const survivingSpouseQuestions = [
    {
      fieldName: 'surviving_itemizedDeductionsExceed',
      questionText: 'Will your N.C. itemized deductions from Page 3, Schedule 1 exceed $27,999?',
    },
    {
      fieldName: 'surviving_childDeductionExceed',
      questionText: 'Will your N.C. child deduction amount from Page 3, Schedule 2 exceed $2,499?',
    },
    {
      fieldName: 'surviving_federalAdjustments',
      questionText: 'Will you have federal adjustments or N.C. deductions from income?',
    },
    {
      fieldName: 'surviving_claimTaxCredits',
      questionText: 'Will you be able to claim any N.C. tax credits or tax credit carryovers?',
    },
  ];

  return (
    <>
      <div className="text-center mb-4">
        <h4 className="mb-0" style={{ fontWeight: 'bold' }}>
          NC-4 Allowance Worksheet
        </h4>
      </div>
      <Card className="mb-4">
        <CardHeader tag="h6" className="bg-light p-2">
          Surviving Spouse
        </CardHeader>
        <CardBody className="p-3">
          {survivingSpouseQuestions.map((q, index) => (
            <WorksheetQuestionRow
              key={q.fieldName}
              questionNumber={index + 1}
              questionText={q.questionText}
              fieldName={q.fieldName}
              value={formData[q.fieldName] as 'yes' | 'no' | ''}
              onChange={handleRadioChange}
            />
          ))}
          <p className="mt-2 mb-0" style={{ fontSize: '0.8rem' }}>
            If you answered “No” to all of the above, STOP HERE and enter <strong>FIVE (5)</strong> as total allowances
            on Form NC-4, Line 1. If you answered “Yes” to any of the above, you may choose to go to Part II to
            determine if you qualify for additional allowances. Otherwise, enter <strong>FIVE (5)</strong> on Form NC-4,
            Line 1.
          </p>
        </CardBody>
      </Card>
      <div className="text-center mb-3">
        <h5 className="mb-0" style={{ fontWeight: 'bold' }}>
          NC-4 Part II
        </h5>
      </div>

      {/* Line 1 */}
      <Row className="mb-3 align-items-center">
        <Col md="9" xs="8">
          <Label htmlFor="nc4_part2_line1" className="mb-0" style={{ fontSize: '0.9rem' }}>
            1. Enter your total estimated N.C. itemized deductions from Page 3, Schedule 1
          </Label>
        </Col>
        <Col md="3" xs="4">
          <InputGroup>
            <InputGroupText>$</InputGroupText>
            <NumberFormat
              {...selectAllOnFocus}
              id="nc4_part2_line1"
              name="part2_line1_itemizedDeductions"
              className="form-control text-right"
              value={
                formData.part2_line1_itemizedDeductions === '' ? undefined : formData.part2_line1_itemizedDeductions
              }
              onValueChange={handleNumericInputChange('part2_line1_itemizedDeductions')}
              thousandSeparator={true}
              decimalScale={2}
              fixedDecimalScale={true}
              allowNegative={false}
              placeholder="0.00"
            />
          </InputGroup>
        </Col>
      </Row>

      {/* Line 2 */}
      <Row className="mb-3 align-items-center">
        <Col md="9" xs="8">
          <Label className="mb-0" style={{ fontSize: '0.9rem' }}>
            2. Enter the applicable N.C. standard deduction based on your filing status.
          </Label>
          <ul style={{ fontSize: '0.8rem', paddingLeft: '20px', margin: '5px 0 0 0' }}>
            <li>$12,750 if Single</li>
            <li>$25,500 if Married Filing Jointly or Surviving Spouse</li>
            <li>$12,750 if Married Filing Separately</li>
            <li>$19,125 if Head of Household</li>
          </ul>
        </Col>
        <Col md="3" xs="4">
          <InputGroup>
            <InputGroupText>$</InputGroupText>
            <NumberFormat
              {...selectAllOnFocus}
              id="nc4_part2_line2"
              name="part2_line2_standardDeduction"
              className="form-control text-right"
              value={formData.part2_line2_standardDeduction === '' ? undefined : formData.part2_line2_standardDeduction}
              onValueChange={handleNumericInputChange('part2_line2_standardDeduction')}
              thousandSeparator={true}
              decimalScale={2}
              fixedDecimalScale={true}
              allowNegative={false}
              placeholder="0.00"
            />
          </InputGroup>
        </Col>
      </Row>

      {/* Line 3 */}
      <Row className="mb-3 align-items-center">
        <Col md="9" xs="8">
          <Label htmlFor="nc4_part2_line3" className="mb-0" style={{ fontSize: '0.9rem' }}>
            3. Subtract Line 2 from Line 1. If Line 1 is less than Line 2, enter ZERO (0).
            <br />
            <small className="text-muted">Enter value directly.</small>
          </Label>
        </Col>
        <Col md="3" xs="4">
          <InputGroup>
            <InputGroupText>$</InputGroupText>
            <NumberFormat
              {...selectAllOnFocus}
              id="nc4_part2_line3"
              name="part2_line3_subtractLine2FromLine1"
              className="form-control text-right"
              value={
                formData.part2_line3_subtractLine2FromLine1 === ''
                  ? undefined
                  : formData.part2_line3_subtractLine2FromLine1
              }
              onValueChange={handleNumericInputChange('part2_line3_subtractLine2FromLine1')}
              thousandSeparator={true}
              decimalScale={2}
              fixedDecimalScale={true}
              allowNegative={true}
              placeholder="0.00"
            />
          </InputGroup>
        </Col>
      </Row>

      {/* Line 4 */}
      <Row className="mb-3 align-items-center">
        <Col md="9" xs="8">
          <Label htmlFor="nc4_part2_line4" className="mb-0" style={{ fontSize: '0.9rem' }}>
            4. Enter an estimate of your total N.C. child deduction amount from Page 3, Schedule 2.
          </Label>
        </Col>
        <Col md="3" xs="4">
          <InputGroup>
            <InputGroupText>$</InputGroupText>
            <NumberFormat
              {...selectAllOnFocus}
              id="nc4_part2_line4"
              name="part2_line4_childDeductionAmount"
              className="form-control text-right"
              value={
                formData.part2_line4_childDeductionAmount === '' ? undefined : formData.part2_line4_childDeductionAmount
              }
              onValueChange={handleNumericInputChange('part2_line4_childDeductionAmount')}
              thousandSeparator={true}
              decimalScale={2}
              fixedDecimalScale={true}
              allowNegative={false}
              placeholder="0.00"
            />
          </InputGroup>
        </Col>
      </Row>

      {/* Line 5 */}
      <Row className="mb-3 align-items-center">
        <Col md="9" xs="8">
          <Label htmlFor="nc4_part2_line5" className="mb-0" style={{ fontSize: '0.9rem' }}>
            5. Enter an estimate of your total federal adjustments to income and N.C. deductions from federal adjusted
            gross income.
          </Label>
        </Col>
        <Col md="3" xs="4">
          <InputGroup>
            <InputGroupText>$</InputGroupText>
            <NumberFormat
              {...selectAllOnFocus}
              id="nc4_part2_line5"
              name="part2_line5_federalAdjustmentsToIncome"
              className="form-control text-right"
              value={
                formData.part2_line5_federalAdjustmentsToIncome === ''
                  ? undefined
                  : formData.part2_line5_federalAdjustmentsToIncome
              }
              onValueChange={handleNumericInputChange('part2_line5_federalAdjustmentsToIncome')}
              thousandSeparator={true}
              decimalScale={2}
              fixedDecimalScale={true}
              allowNegative={false}
              placeholder="0.00"
            />
          </InputGroup>
        </Col>
      </Row>

      {/* Line 6 */}
      <Row className="mb-3 align-items-center">
        <Col md="9" xs="8">
          <Label htmlFor="nc4_part2_line6" className="mb-0" style={{ fontSize: '0.9rem' }}>
            6. Add Lines 3, 4, and 5.
            <br />
            <small className="text-muted">Enter value directly.</small>
          </Label>
        </Col>
        <Col md="3" xs="4">
          <InputGroup>
            <InputGroupText>$</InputGroupText>
            <NumberFormat
              {...selectAllOnFocus}
              id="nc4_part2_line6"
              name="part2_line6_addLines345"
              className="form-control text-right"
              value={formData.part2_line6_addLines345 === '' ? undefined : formData.part2_line6_addLines345}
              onValueChange={handleNumericInputChange('part2_line6_addLines345')}
              thousandSeparator={true}
              decimalScale={2}
              fixedDecimalScale={true}
              allowNegative={false}
              placeholder="0.00"
            />
          </InputGroup>
        </Col>
      </Row>

      {/* Line 7 */}
      <Row className="mb-3 align-items-center">
        <Col md="9" xs="8">
          <Label htmlFor="nc4_part2_line7" className="mb-0" style={{ fontSize: '0.9rem' }}>
            7. Enter an estimate of your nonwage income (such as interest or dividends).
          </Label>
        </Col>
        <Col md="3" xs="4">
          <InputGroup>
            <InputGroupText>$</InputGroupText>
            <NumberFormat
              {...selectAllOnFocus}
              id="nc4_part2_line7"
              name="part2_line7_nonwageIncome"
              className="form-control text-right"
              value={formData.part2_line7_nonwageIncome === '' ? undefined : formData.part2_line7_nonwageIncome}
              onValueChange={handleNumericInputChange('part2_line7_nonwageIncome')}
              thousandSeparator={true}
              decimalScale={2}
              fixedDecimalScale={true}
              allowNegative={false}
              placeholder="0.00"
            />
          </InputGroup>
        </Col>
      </Row>

      {/* Line 8 */}
      <Row className="mb-3 align-items-center">
        <Col md="9" xs="8">
          <Label htmlFor="nc4_part2_line8" className="mb-0" style={{ fontSize: '0.9rem' }}>
            8. Enter an estimate of your N.C. additions to federal adjusted gross income.
          </Label>
        </Col>
        <Col md="3" xs="4">
          <InputGroup>
            <InputGroupText>$</InputGroupText>
            <NumberFormat
              {...selectAllOnFocus}
              id="nc4_part2_line8"
              name="part2_line8_ncAdditionsToAgi"
              className="form-control text-right"
              value={formData.part2_line8_ncAdditionsToAgi === '' ? undefined : formData.part2_line8_ncAdditionsToAgi}
              onValueChange={handleNumericInputChange('part2_line8_ncAdditionsToAgi')}
              thousandSeparator={true}
              decimalScale={2}
              fixedDecimalScale={true}
              allowNegative={false}
              placeholder="0.00"
            />
          </InputGroup>
        </Col>
      </Row>

      {/* Line 9 */}
      <Row className="mb-3 align-items-center">
        <Col md="9" xs="8">
          <Label htmlFor="nc4_part2_line9" className="mb-0" style={{ fontSize: '0.9rem' }}>
            9. Add Lines 7 and 8.
            <br />
            <small className="text-muted">Enter value directly.</small>
          </Label>
        </Col>
        <Col md="3" xs="4">
          <InputGroup>
            <InputGroupText>$</InputGroupText>
            <NumberFormat
              {...selectAllOnFocus}
              id="nc4_part2_line9"
              name="part2_line9_addLines78"
              className="form-control text-right"
              value={formData.part2_line9_addLines78 === '' ? undefined : formData.part2_line9_addLines78}
              onValueChange={handleNumericInputChange('part2_line9_addLines78')}
              thousandSeparator={true}
              decimalScale={2}
              fixedDecimalScale={true}
              allowNegative={false}
              placeholder="0.00"
            />
          </InputGroup>
        </Col>
      </Row>

      {/* Line 10 */}
      <Row className="mb-3 align-items-center">
        <Col md="9" xs="8">
          <Label htmlFor="nc4_part2_line10" className="mb-0" style={{ fontSize: '0.9rem' }}>
            10. Subtract Line 9 from Line 6 (Do not enter less than zero).
            <br />
            <small className="text-muted">Enter value directly.</small>
          </Label>
        </Col>
        <Col md="3" xs="4">
          <InputGroup>
            <InputGroupText>$</InputGroupText>
            <NumberFormat
              {...selectAllOnFocus}
              id="nc4_part2_line10"
              name="part2_line10_subtractLine9FromLine6"
              className="form-control text-right"
              value={
                formData.part2_line10_subtractLine9FromLine6 === ''
                  ? undefined
                  : formData.part2_line10_subtractLine9FromLine6
              }
              onValueChange={handleNumericInputChange('part2_line10_subtractLine9FromLine6')}
              thousandSeparator={true}
              decimalScale={2}
              fixedDecimalScale={true}
              allowNegative={true}
              placeholder="0.00"
            />
          </InputGroup>
        </Col>
      </Row>

      {/* Line 11 */}
      <Row className="mb-3 align-items-center">
        <Col md="9" xs="8">
          <Label htmlFor="nc4_part2_line11_allowance" className="mb-0" style={{ fontSize: '0.9rem' }}>
            11. Divide the amount on Line 10 by $2,500. Round down to whole number.
            <br />
            <small className="text-muted">Ex. $3,900 ÷ $2,500 = 1.56 rounds down to 1. Enter value directly.</small>
          </Label>
        </Col>
        <Col md="3" xs="4">
          <NumberFormat
            {...selectAllOnFocus}
            id="nc4_part2_line11_allowance"
            name="part2_line11_divideLine10By2500"
            className="form-control text-right"
            value={
              formData.part2_line11_divideLine10By2500 === '' ? undefined : formData.part2_line11_divideLine10By2500
            }
            onValueChange={handleNumericInputChange('part2_line11_divideLine10By2500')}
            thousandSeparator={false}
            decimalScale={0}
            allowNegative={false}
            placeholder="0"
          />
        </Col>
      </Row>

      {/* Line 12 */}
      <Row className="mb-3 align-items-center">
        <Col md="9" xs="8">
          <Label htmlFor="nc4_part2_line12" className="mb-0" style={{ fontSize: '0.9rem' }}>
            12. Enter the amount of your estimated N.C. tax credits.
          </Label>
        </Col>
        <Col md="3" xs="4">
          <InputGroup>
            <InputGroupText>$</InputGroupText>
            <NumberFormat
              {...selectAllOnFocus}
              id="nc4_part2_line12"
              name="part2_line12_estimatedNcTaxCredits"
              className="form-control text-right"
              value={
                formData.part2_line12_estimatedNcTaxCredits === ''
                  ? undefined
                  : formData.part2_line12_estimatedNcTaxCredits
              }
              onValueChange={handleNumericInputChange('part2_line12_estimatedNcTaxCredits')}
              thousandSeparator={true}
              decimalScale={2}
              fixedDecimalScale={true}
              allowNegative={false}
              placeholder="0.00"
            />
          </InputGroup>
        </Col>
      </Row>

      {/* Line 13 */}
      <Row className="mb-3 align-items-center">
        <Col md="9" xs="8">
          <Label htmlFor="nc4_part2_line13_allowance" className="mb-0" style={{ fontSize: '0.9rem' }}>
            13. Divide the amount on Line 12 by $109. Round down to whole number.
            <br />
            <small className="text-muted">Ex. $200 ÷ $109 = 1.83 rounds down to 1. Enter value directly.</small>
          </Label>
        </Col>
        <Col md="3" xs="4">
          <NumberFormat
            {...selectAllOnFocus}
            id="nc4_part2_line13_allowance"
            name="part2_line13_divideLine12By109"
            className="form-control text-right"
            value={formData.part2_line13_divideLine12By109 === '' ? undefined : formData.part2_line13_divideLine12By109}
            onValueChange={handleNumericInputChange('part2_line13_divideLine12By109')}
            thousandSeparator={false}
            decimalScale={0}
            allowNegative={false}
            placeholder="0"
          />
        </Col>
      </Row>

      {/* Line 14 */}
      <Row className="mb-3 align-items-center">
        <Col md="9" xs="8">
          <Label htmlFor="nc4_part2_line14_allowance" className="mb-0" style={{ fontSize: '0.9rem' }}>
            14. If filing as Single, Head of Household, or Married Filing Separately, enter zero (0) on this line.{' '}
            <br /> If filing as Surviving Spouse, enter 5. <br /> If filing as Married Filing Jointly, enter the
            appropriate number from either (a), (b), (c), (d), (e), or (f) below.
          </Label>
          <ul style={{ fontSize: '0.8rem', paddingLeft: '20px', margin: '5px 0 0 0', listStyleType: 'lower-alpha' }}>
            <li>
              Your spouse expects to have combined wages and taxable retirement benefits of less than or equal to $250
              for N.C. purposes, enter 5. (Taxable retirement benefits do not include: Bailey, Certain Military
              Retirement, Social Security, and Railroad Retirement)
            </li>
            <li>
              Your spouse expects to have combined wages and taxable retirement benefits of more than $250 but less than
              or equal to $2,750, enter 4.
            </li>
            <li>
              Your spouse expects to have combined wages and taxable retirement benefits of more than $2,750 but less
              than or equal to $5,250, enter 3.
            </li>
            <li>
              Your spouse expects to have combined wages and taxable retirement benefits of more than $5,250 but less
              than or equal to $7,750, enter 2.
            </li>
            <li>
              Your spouse expects to have combined wages and taxable retirement benefits of more than $7,750 but less
              than or equal to $10,250, enter 1.
            </li>
            <li>
              Your spouse expects to have combined wages and taxable retirement benefits of more than $10,250, enter 0.
            </li>
          </ul>
        </Col>
        <Col md="3" xs="4">
          <NumberFormat
            {...selectAllOnFocus}
            id="nc4_part2_line14_allowance"
            name="part2_line14_additionalAllowances"
            className="form-control text-right"
            value={
              formData.part2_line14_additionalAllowances === '' ? undefined : formData.part2_line14_additionalAllowances
            }
            onValueChange={handleNumericInputChange('part2_line14_additionalAllowances')}
            thousandSeparator={false}
            decimalScale={0}
            allowNegative={false}
            placeholder="0"
          />
        </Col>
      </Row>

      {/* Line 15 */}
      <Row className="mb-3 align-items-center">
        <Col md="9" xs="8">
          <Label htmlFor="nc4_part2_line15_allowance" className="mb-0" style={{ fontSize: '0.9rem' }}>
            15. Add Lines 11, 13, and 14, and enter the total here.
            <br />
            <small className="text-muted">Enter value directly.</small>
          </Label>
        </Col>
        <Col md="3" xs="4">
          <NumberFormat
            {...selectAllOnFocus}
            id="nc4_part2_line15_allowance"
            name="part2_line15_totalAllowancesPartII"
            className="form-control text-right"
            value={
              formData.part2_line15_totalAllowancesPartII === ''
                ? undefined
                : formData.part2_line15_totalAllowancesPartII
            }
            onValueChange={handleNumericInputChange('part2_line15_totalAllowancesPartII')}
            thousandSeparator={false}
            decimalScale={0}
            allowNegative={false}
            placeholder="0"
          />
        </Col>
      </Row>

      {/* Line 16 */}
      <Row className="mb-3 align-items-center">
        <Col md="9" xs="8">
          <Label htmlFor="nc4_part2_line16_allowance" className="mb-0" style={{ fontSize: '0.9rem' }}>
            16. If you completed this worksheet on the basis of Married Filing Jointly, the total number of allowances
            determined on Line 15 may be split between you and your spouse, however, you choose. Enter the number of
            allowances from Line 15 that your spouse plans to claim.
          </Label>
        </Col>
        <Col md="3" xs="4">
          <NumberFormat
            {...selectAllOnFocus}
            id="nc4_part2_line16_allowance"
            name="part2_line16_spouseAllowancesToClaim"
            className="form-control text-right"
            value={
              formData.part2_line16_spouseAllowancesToClaim === ''
                ? undefined
                : formData.part2_line16_spouseAllowancesToClaim
            }
            onValueChange={handleNumericInputChange('part2_line16_spouseAllowancesToClaim')}
            thousandSeparator={false}
            decimalScale={0}
            allowNegative={false}
            placeholder="0"
          />
        </Col>
      </Row>

      {/* Line 17 */}
      <Row className="mb-3 align-items-center">
        <Col md="9" xs="8">
          <Label htmlFor="nc4_part2_line17_allowance" className="mb-0" style={{ fontSize: '0.9rem' }}>
            17. Subtract Line 16 from Line 15 and enter the total number of allowances here and on Line 1 of your Form
            NC-4, Employee’s Withholding Allowance Certificate
            <br />
            <small className="text-muted">Enter value directly.</small>
          </Label>
        </Col>
        <Col md="3" xs="4">
          <NumberFormat
            {...selectAllOnFocus}
            id="nc4_part2_line17_allowance"
            name="part2_line17_finalTotalAllowances"
            className="form-control text-right"
            value={
              formData.part2_line17_finalTotalAllowances === '' ? undefined : formData.part2_line17_finalTotalAllowances
            }
            onValueChange={handleNumericInputChange('part2_line17_finalTotalAllowances')}
            thousandSeparator={false}
            decimalScale={0}
            allowNegative={false}
            placeholder="0"
          />
        </Col>
      </Row>
    </>
  );
};

export default NC4StepThree;
